// Main entry point for the application
// Import settings.js to bundle it (prevents users from easily modifying it)
import '/settings.js';
import { initializeApp } from './app.js';
import { initializeAllCookies } from './info/cookies-utils.js';

/**
 * Initialize Google Tag Manager - GDPR COMPLIANT
 * Called AFTER user consent is obtained
 */
function initializeGTM() {
    // Check if already initialized (prevent double-loading)
    if (window.gtmInitialized) {
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log('[GTM] Already initialized, skipping');
        }
        return;
    }

    // Only load GTM if user has accepted analytics
    if (window.CookieConsent && !window.CookieConsent.acceptedCategory('analytics')) {
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log('[GTM] Analytics not accepted, skipping GTM initialization');
        }
        return;
    }

    if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
        console.log('[GTM] Initializing Google Tag Manager (consent granted)');
    }
    window.gtmInitialized = true;

    // Load GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-WND9CZF3';
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
    });
}

/**
 * Setup GTM initialization listener
 * GTM will initialize when user accepts analytics cookies
 */
function setupGTMListener() {
    // Listen for consent changes (fires when user accepts/rejects)
    window.addEventListener('cc:onChange', (event) => {
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log('[GTM] Cookie consent changed:', event.detail);
        }
        // Initialize GTM if user just accepted analytics
        if (window.CookieConsent.acceptedCategory('analytics')) {
            initializeGTM();
        }
    });

    // Also listen for first consent (when user clicks a button for the first time)
    window.addEventListener('cc:onConsent', (event) => {
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log('[GTM] First consent given:', event.detail);
        }
        // Initialize GTM if user accepted analytics
        if (window.CookieConsent.acceptedCategory('analytics')) {
            initializeGTM();
        }
    });

    // Check if user already has existing consent (returning visitor)
    if (window.CookieConsent && window.CookieConsent.acceptedCategory('analytics')) {
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log('[GTM] Existing consent found, initializing GTM');
        }
        initializeGTM();
    }
}

// Initialize cookies library config on page load
window.CookieUtils = {
    getCookieConsentConfig: async (domainServices = {}) => {
        // getCookieConsentConfig will be initialized by the module
        return {};
    },
    initializeCookieConsent: async (config) => {
        // Will be set by cookies-utils module
    },
    loadCookieTranslations: async () => {
        // Will be set by cookies-utils module
    }
};

document.addEventListener('DOMContentLoaded', async () => {
    try {
        // Initialize cookies FIRST so guide can wait for consent
        await initializeAllCookies();

        // Then initialize the app (which may show the guide)
        await initializeApp();

        // Setup GTM listener - GTM will load when user accepts analytics
        setupGTMListener();

    } catch (error) {
        console.error('Failed to initialize application:', error);
        // Also show error in UI if possible
        const loadingOverlay = document.getElementById('app-loading');
        if (loadingOverlay) {
            loadingOverlay.innerHTML = `
                <div class="app-loading-content" style="color: red; text-align: center;">
                    <h2>Error Loading App</h2>
                    <p>${error.message || 'Unknown error'}</p>
                    <p style="font-size: 12px; margin-top: 10px;">Check browser console for details</p>
                </div>
            `;
        }
        // Re-throw to ensure it's visible
        throw error;
    }
});
